import AirbrakeClient from 'airbrake-js'
import type { UserDto } from '../dtos/user'
import type { DomainDto } from '../dtos/domain'

// A leaf: the reporting sink every boundary and loader writes to. It imports nothing else from the
// app by value, so new code may depend on it without depending on a legacy layer.

/**
 * Errors that monitoring cannot act on. A request that never reached the server is the user's
 * connection, not the app, and is reported to the user as a transient failure instead.
 */
export const NON_ACTIONABLE_ERRORS: readonly RegExp[] = [/Network Error/]

/**
 * The name of the environment this build was configured for, which is not its compile mode.
 *
 * A deployed build always compiles for production, so `NODE_ENV` cannot answer "which
 * environment is this": `AIRBRAKE_ENV` is declared per environment file for exactly that
 * question. The fallbacks keep a local run, which declares neither, out of remote reporting.
 */
const REPORTING_ENVIRONMENT = process.env.AIRBRAKE_ENV || process.env.NODE_ENV || 'development'

let airbrakeClient: AirbrakeClient | undefined
export const getAirbrakeClient = (): AirbrakeClient => {
    if (!airbrakeClient) {
        airbrakeClient = new AirbrakeClient({
            projectId: 477859,
            projectKey: '5aed6fdc6173c89e04aa4b4a40208bfc',
            environment: REPORTING_ENVIRONMENT,
            ignoreWindowError: true,
        })
    }

    return airbrakeClient
}

/**
 * The reference id of each reported error, by the error value. A screen that shows a failure looks
 * its id up here, so the id the user reads is the one on the report and the error is reported once.
 * Only an object can be a key; a thrown primitive gets an id on its report but cannot be looked up.
 */
const referenceIds = new WeakMap<object, string>()

const newReferenceId = (): string => {
    const random = globalThis.crypto?.randomUUID?.()
    if (random) {
        return random
    }
    // A runtime without the Web Crypto API still gets an id a support conversation can quote.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** The support reference id of an error that was reported, or undefined when it was not. */
export const errorReferenceId = (error: unknown): string | undefined =>
    typeof error === 'object' && error !== null ? referenceIds.get(error) : undefined

/**
 * Reports an error to monitoring and returns its support reference id. The id is generated here,
 * attached to the report, and remembered for the error value, so a failure screen can show the same
 * id the report carries. Outside production the report goes to the console with the same id.
 */
export const streamApplicationError = (
    error: any,
    info: any,
    currentUser?: UserDto,
    currentDomain?: DomainDto,
): string => {
    const referenceId = newReferenceId()
    if (typeof error === 'object' && error !== null) {
        referenceIds.set(error, referenceId)
    }

    if (REPORTING_ENVIRONMENT !== 'production') {
        console.error(error, { ...info, referenceId })
        return referenceId
    }

    /**
     * Do not send non-actionable errors to remote
     */
    if (error instanceof Error && NON_ACTIONABLE_ERRORS.some((r) => r.test(error.message))) {
        return referenceId
    }

    /**
     * Fix for Notifier.notify() ignoring passed
     * annotations when error is passed directly.
     */
    const wrappedError = new Error(error)
    if (error instanceof Error) {
        wrappedError.name = error.name ?? wrappedError.name
        wrappedError.message = error.message ?? wrappedError.message
        wrappedError.stack = error.stack ?? wrappedError.stack
    }

    /**
     * componentStack comes in as a line break
     * separated backtrace string. Break apart
     * to ensure better readability in airbrake
     * dashboard.
     */
    let componentStack = info?.componentStack
    if (typeof componentStack === 'string') {
        componentStack = componentStack
            .split(/\n/)
            .map((s) => s.trim())
            .filter((s) => s)
    }

    /**
     * Inject any pertinent information for the
     * current client session.
     */
    const session = {
        user_id: currentUser?.id,
        domain_id: currentDomain?.id,
    }

    getAirbrakeClient().notify({
        error: wrappedError,
        params: {
            info: {
                ...info,
                componentStack,
            },
            referenceId,
        },
        session,
    })

    return referenceId
}
